import { Injectable } from '@angular/core';
import { AngularFirestore, AngularFirestoreCollection } from '@angular/fire/firestore';
import { AngularFireAuth } from '@angular/fire/auth';
import { User } from './user';
import { from, Observable, throwError, of } from 'rxjs';
import { switchMap, catchError, map, tap } from 'rxjs/operators';
import firebase from 'firebase/app';

const COLLECTION_NAME = 'users';

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  private collection: AngularFirestoreCollection<User> = this.afs.collection(COLLECTION_NAME);

  constructor(
    private afs: AngularFirestore,
    private afAuth: AngularFireAuth
  ) { }

  register(user: User): Observable<boolean> {
    return from(this.afAuth.createUserWithEmailAndPassword(user.email, user.password))
      .pipe(
        switchMap((u: any) => {
          delete user.password;
          return this.collection.doc(u.user.uid)
            .set({ ...user, id: u.user.uid }).then(() => true)
        }
        ),
        catchError((err) => throwError(err))
      )
  }

  login(email: string, password: string): Observable<User> {
    return from(this.afAuth.signInWithEmailAndPassword(email, password))
      .pipe(
        switchMap((u: any) => this.collection.doc<User>(u.user.uid).valueChanges()),
        catchError(() => throwError('Invalid credentials or user is not registered.'))
      )
  }

  logout() {
    this.afAuth.signOut();
  }

  getUser(): Observable<User> {
    return this.afAuth.authState
      .pipe(
        switchMap(u => (u) ?
          this.collection.doc<User>(u.uid).valueChanges() : of(null))
      )
  }

  authenticated(): Observable<boolean> {
    return this.afAuth.authState
      .pipe(map(u => (u) ? true : false))
  }

  async updateUserData(u) {
    try {
      const newUser: User = {
        id: u.user.uid,
        firstname: u.user.displayName,
        lastname: '',
        email: u.user.email,
      };

      await this.collection.doc(u.user.uid).set(newUser);
      return newUser;
    }
    catch (e) {
      throw new Error(e);
    }
  }

  async loginWithGoogleAccount() {
    try {
      const provider = new firebase.auth.GoogleAuthProvider();
      let credentials = await this.afAuth.signInWithPopup(provider);
      let user: User = await this.updateUserData(credentials);
      return user;
    }
    catch (e) {
      throw new Error(e);
    }
  }

  loginGoogle(): Observable<User> {
    return from(this.loginWithGoogleAccount());
  }
}
